import { t } from "../lib/i18n";
import React, { useState, useEffect } from 'react';
import api from '../api';
import { dispatchFinancialsChangeEvent } from '../financialsEventSystem';

interface Transaction {
  id?: string;
  transaction_type: string;
  description?: string;
  amount: number;
  due_date?: string;
  paid_date?: string;
  category: 'income' | 'expense';
}

interface EditTransactionModalProps {
  transaction: Transaction | null;
  onClose: () => void;
}

const EditTransactionModal: React.FC<EditTransactionModalProps> = ({ transaction, onClose }) => {
  const [transactionType, setTransactionType] = useState('');
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [paidDate, setPaidDate] = useState('');
  const [category, setCategory] = useState<'income' | 'expense'>('expense');
  const [message, setMessage] = useState('');

  useEffect(() => {
    if (transaction) {
      setTransactionType(transaction.transaction_type);
      setDescription(transaction.description || '');
      setAmount(transaction.amount.toString());
      setDueDate(transaction.due_date ? new Date(transaction.due_date).toISOString().split('T')[0] : '');
      setPaidDate(transaction.paid_date ? new Date(transaction.paid_date).toISOString().split('T')[0] : '');
      setCategory(transaction.category);
    }
  }, [transaction]);

  const handleSave = async () => {
    if (!transaction) return;

    try {
      await api.put(`/financials/${transaction.id}`, {
        transaction_type: transactionType,
        description: description,
        amount: parseFloat(amount),
        due_date: dueDate,
        paid_date: paidDate,
        category: category,
      });
      setMessage('Transaction updated successfully!');
      dispatchFinancialsChangeEvent();
      onClose();
    } catch (error: any) {
      console.error('Error updating transaction:', error);
      setMessage(error.response?.data?.detail || 'Failed to update transaction.');
    }
  };

  if (!transaction) {
    return null;
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-bg-secondary p-6 rounded-lg shadow-lg w-full max-w-md">
        <h2 className="text-2xl font-bold text-text-primary mb-4">{t("editTransactionModal.k1")}</h2>
        {message && <p className="text-danger mb-4">{message}</p>}
        <div className="mb-4">
          <label htmlFor="transactionType" className="block text-text-secondary text-sm font-bold mb-2">{t("editTransactionModal.k2")}</label>
          <input
            type="text"
            id="transactionType"
            value={transactionType}
            onChange={(e) => setTransactionType(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="description" className="block text-text-secondary text-sm font-bold mb-2">{t("editTransactionModal.k3")}</label>
          <input
            type="text"
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="amount" className="block text-text-secondary text-sm font-bold mb-2">{t("editTransactionModal.k4")}</label>
          <input
            type="number"
            id="amount"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="dueDate" className="block text-text-secondary text-sm font-bold mb-2">{t("editTransactionModal.k5")}</label>
          <input
            type="date"
            id="dueDate"
            value={dueDate}
            onChange={(e) => setDueDate(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="paidDate" className="block text-text-secondary text-sm font-bold mb-2">{t("editTransactionModal.k6")}</label>
          <input
            type="date"
            id="paidDate"
            value={paidDate}
            onChange={(e) => setPaidDate(e.target.value)}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          />
        </div>
        <div className="mb-4">
          <label htmlFor="category" className="block text-text-secondary text-sm font-bold mb-2">{t("editTransactionModal.k7")}</label>
          <select
            id="category"
            value={category}
            onChange={(e) => setCategory(e.target.value as 'income' | 'expense')}
            className="shadow appearance-none border rounded w-full py-2 px-3 text-text-primary leading-tight focus:outline-none focus:shadow-outline"
          >
            <option value="income">{t("editTransactionModal.k8")}</option>
            <option value="expense">{t("editTransactionModal.k9")}</option>
          </select>
        </div>
        <div className="flex justify-end gap-4">
          <button onClick={onClose} className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600">
            {t("editTransactionModal.k10")} </button>
          <button onClick={handleSave} className="bg-primary text-white px-4 py-2 rounded hover:bg-primary-dark">
            {t("editTransactionModal.k11")} </button>
        </div>
      </div>
    </div>
  );
};

export default EditTransactionModal;
